import Vue from "vue";
import { mapState } from "vuex";

import Moment from "moment";

import buttonScComponent from "@/components/child/form/buttonSc.vue";
import comboComponent from "@/components/child/form/combo.vue";
import fieldsetComponent from "@/components/child/form/fieldset.vue";
import moedaComponent from "@/components/child/form/moeda.vue";
import gridComponent from "@/components/child/grid/grid.vue";
import { GridAction } from "@/components/child/grid/gridAction";
import { GridColors, GridColumn, GridColumnType } from "@/components/child/grid/gridColumn";
import { Component, Prop } from "@/decorators";
import TipoReceitaArquivo from "@/models/enum/tipoReceitaArquivo";
import ContaReceberModel from "@/models/financeiro/contaReceberModel";
import ReceitaArquivoModel from "@/models/receitaArquivoModel";
import UsuarioModel from "@/models/usuarioModel";
import tipoVendaService from "@/services/tipoVendaService";
import { AppState, SessionActions } from "@/store/store";
import { AlertColor } from "@/utils/common/alert";
import arithmeticHelper from "@/utils/common/arithmeticHelper";
import { editDate, editDateWithTime } from "@/utils/common/date";
import Delay from "@/utils/common/delay";

import RecebimentoComponent from "../../recebimentos/list";
import recebimentoComponent from "../../recebimentos/list.vue";
import ShortcutComponent from "../../shortcut/shortcut";
import shortcutComponent from "../../shortcut/shortcut.vue";
import PagamentoComponent from "../pagamento";
import pagamentoComponent from "../pagamento.vue";

import ParcelaComponent from "./parcela";
import parcelaComponent from "./parcela.vue";

import "../../crud/crud.scss";

@Component({
    components: {
        comboComponent,
        moedaComponent,
        buttonScComponent,
        gridComponent,
        shortcutComponent,
        recebimentoComponent,
        pagamentoComponent,
        parcelaComponent,
        fieldsetComponent,
    },
    computed: {
        ...mapState({
            usuarioLogado: (state: AppState) => state.session.usuarioLogado,
        }),
    },
})
export default class FinanceiroComponent extends Vue {
    usuarioLogado: UsuarioModel;

    private shortcutComponentParcela: ShortcutComponent = null;
    private shortcutComponentRecebimento: ShortcutComponent = null;
    private parcelaComponent: ParcelaComponent = null;
    private pagamentoComponent: PagamentoComponent = null;
    private recebimentoComponent: RecebimentoComponent = null;

    model = new ContaReceberModel();

    @Prop(Number) situacaoVenda: number;
    @Prop(String) codigoVenda: string;
    @Prop({ type: Boolean, default: false }) isConsulta: boolean;
    @Prop({ type: Boolean, default: true }) permiteRemover: boolean;
    @Prop({ type: Array, default: () => [] }) arquivosFinanceiros: ReceitaArquivoModel[];

    private gridData = [];
    private addRecebimentoNaParcelaIndex = null;
    private editingParcelaIndex = null;
    private saldoTotal = 0;
    private desconto = 0;
    private acrescimo = 0;
    private vendafaturada = false;
    private dataHoraAbertura = "";
    private dataHoraFechamento = "";
    private nomeUsuario = "";
    tipoVendaOptions: Array<Object> = [];
    tipoVenda = 1;

    private extraActions: Array<GridAction[]> = [];
    private extraActionsPagar: Array<GridAction> = [];

    get gridColumnsParcelas(): Array<GridColumn> {
        return [
            new GridColumn("descricao", this.$t("__.ts.descricao") as string, GridColumnType.String),
            new GridColumn("dataVencimento", this.$t("__.ts.vencimento") as string, GridColumnType.Date),
            new GridColumn("valorTotal", this.$t("__.ts.vlr") as string, GridColumnType.Money),
            new GridColumn("saldoGrid", this.$t("__.ts.saldo") as string, GridColumnType.Money),
        ];
    }

    get gridColumnsRecebimentos(): Array<GridColumn> {
        return [
            new GridColumn("dataMovimento", this.$t("__.ts.recebimento") as string, GridColumnType.Date),
            new GridColumn("formaPagamentoDescricao", this.$t("__.ts.formPgto") as string, GridColumnType.String),
            new GridColumn("desconto", this.$t("__.ts.descont") as string, GridColumnType.Money),
            new GridColumn("juros", this.$t("__.ts.juros") as string, GridColumnType.Money),
            new GridColumn("valor", this.$t("__.ts.vlr") as string, GridColumnType.Money),
            new GridColumn("observacao", this.$t("__.ts.observacao") as string, GridColumnType.String),
            new GridColumn(
                "gatewayPagamentoDescricao",
                this.$t("__.Components.parent.venda.vendaForm_vue_html.origem") as string,
                GridColumnType.String,
            ),
        ];
    }

    private conditionShowSubData(data: object) {
        return data["movimentacoes"] && data["movimentacoes"]["length"] > 0;
    }

    private async loadGridData() {
        this.gridData = [];

        this.extraActions.splice(0);
        this.extraActionsPagar.splice(0);

        if (this.model.lancamentosVinculados.length > 0) {
            this.gridData = this.model.lancamentosVinculados;

            this.nomeUsuario = this.gridData[0]?.movimentacoes[0]?.sessaoCaixa?.nomeUsuario ?? "";
            this.dataHoraAbertura =
                editDateWithTime(this.gridData[0]?.movimentacoes[0]?.sessaoCaixa?.dataHoraAbertura) ?? "";
            this.dataHoraFechamento =
                editDateWithTime(this.gridData[0]?.movimentacoes[0]?.sessaoCaixa?.dataHoraFechamento) ?? "";

            this.model.lancamentosVinculados.forEach(p => {
                let action = new GridAction(
                    "add-recebimento",
                    "Adicionar recebimento",
                    "fa fa-money-bill",
                    GridColors.GREEN,
                );
                this.calcularSaldoParcela(p);

                if (p.saldoGrid == 0) {
                    action = new GridAction("", "Pago", "fa fa-check", GridColors.GREEN);
                }

                this.extraActionsPagar.push(action);
            });

            this.extraActions.push(this.extraActionsPagar);
        } else {
            while (!this.$refs.recebimentoComponent) await Delay(5);
            this.recebimentoComponent = this.$refs.recebimentoComponent as RecebimentoComponent;
            this.recebimentoComponent.vendafaturada = this.vendafaturada;

            this.recebimentoComponent.show(this.model.movimentacoes);
        }
    }

    private onExtraActions(name: string, data: ContaReceberModel) {
        if (name.trim() == "add-recebimento") {
            this.showAddRecebimento();

            const valorJaPago = data.movimentacoes.reduce((acc, p) => acc + p.valor, 0);
            this.pagamentoComponent.load(data["valorTotal"] - valorJaPago);

            this.addRecebimentoNaParcelaIndex = this.gridData.indexOf(data);
        }
    }

    private async onEdit(data: object) {
        if (this.model.lancamentosVinculados.length > 0) {
            this.parcelaComponent.model = { ...data } as ContaReceberModel;
            await this.parcelaComponent.loadLinkBoleto();
            this.editingParcelaIndex = this.gridData.indexOf(data);

            this.shortcutComponentParcela.show();
            this.shortcutComponentParcela.title = this.$t("__.ts.addParcela") as string;
        }
    }

    private onRemovePagamento(model: object) {
        if (this.model) {
            this.model.movimentacoes = this.model.movimentacoes.filter(p => p != model);
            this.loadGridData();
        }
    }

    private async onRemove(data: object) {
        if (data["saldoGrid"] == data["valorTotal"]) {
            const index = this.gridData.indexOf(data);

            if (this.model.lancamentosVinculados.length > 0) {
                this.model.lancamentosVinculados.splice(index, 1);
            } else {
                this.model.movimentacoes.splice(index, 1);
            }

            this.loadGridData();
        } else {
            await this.$showAlert({
                title: "Parcela não pode ser excluída",
                html: `Não é possível excluir esta parcela, pois ela ja foi paga.`,
                icon: "warning",
                confirmButtonColor: AlertColor.GRAY,
                allowOutsideClick: false,
            });
        }
    }

    private showAddParcela() {
        this.shortcutComponentParcela.show();
        this.shortcutComponentParcela.title = this.$t("__.ts.addParcela") as string;

        const valorJaParcelado = this.model.lancamentosVinculados.reduce((acc, p) => acc + p.valorTotal, 0);

        this.parcelaComponent.load(this.saldoTotal - valorJaParcelado, this.desconto);
    }

    private async onAddParcela() {
        const isValid = await this.parcelaComponent.validate();
        if (isValid) {
            if (this.editingParcelaIndex != null) {
                this.model.lancamentosVinculados[this.editingParcelaIndex] = this.parcelaComponent.model;
            } else {
                this.model.lancamentosVinculados.push(this.parcelaComponent.model);
            }

            this.loadGridData();

            this.shortcutComponentParcela.hide();

            this.editingParcelaIndex = null;
        }
    }

    private hideAddParcela() {
        this.editingParcelaIndex = null;
    }

    private showAddRecebimento() {
        this.shortcutComponentRecebimento.show();
        this.shortcutComponentRecebimento.title = this.$t("__.ts.recebimento") as string;

        this.addRecebimentoNaParcelaIndex = null;

        const saldoTotalMovimentacoes = this.model.movimentacoes.reduce((acc, p) => acc + p.valor, 0);
        const saldoTotal = this.saldoTotal >= saldoTotalMovimentacoes ? this.saldoTotal - saldoTotalMovimentacoes : 0;
        // sempre vamos user o valor final da venda e não será possível aplicar descontos ou acrescimos pela tela de pagamento
        this.pagamentoComponent.load(saldoTotal);
    }

    private async onAddRecebimento() {
        const isValid = await this.pagamentoComponent.validate();
        if (isValid) {
            if (this.addRecebimentoNaParcelaIndex != null) {
                const recebimento = this.pagamentoComponent.getModel();
                this.model.lancamentosVinculados[this.addRecebimentoNaParcelaIndex].movimentacoes.push(recebimento);
            } else {
                if (this.pagamentoComponent.isBoletoPrazo) {
                    const recebimentoParcelado = this.pagamentoComponent.getMetodoRecebimentoParcelado();
                    const valorTotal = this.saldoTotal - this.desconto + this.acrescimo;

                    const valorJaRecebido = this.model.movimentacoes.reduce((acc, p) => acc + p.valor, 0);
                    const valorJaParcelado = this.model.lancamentosVinculados.reduce((acc, p) => acc + p.valorTotal, 0);
                    const valorRestante = arithmeticHelper.round(valorTotal - valorJaRecebido - valorJaParcelado);

                    if (valorJaRecebido > 0) {
                        const contaRecebida = new ContaReceberModel();
                        contaRecebida.contabilizar = false;
                        contaRecebida.dataLancamento = new Date();
                        contaRecebida.dataVencimento = new Date();
                        contaRecebida.descricao = `Venda Cód: ${this.codigoVenda} (recebido)`;
                        contaRecebida.valorTotal = arithmeticHelper.round(valorJaRecebido);
                        contaRecebida.movimentacoes = this.model.movimentacoes;
                        this.model.lancamentosVinculados.push(contaRecebida);
                        this.model.movimentacoes = [];
                    }

                    recebimentoParcelado.itens.forEach((p, index) => {
                        const dataVencimento = Moment().add(p.diasVencimento, "days").toDate();
                        const conta = new ContaReceberModel();
                        conta.contabilizar = false;
                        conta.dataLancamento = new Date();
                        conta.descricao = `Venda Cód: ${this.codigoVenda} (${index + 1}/${recebimentoParcelado.itens.length})`;
                        conta.valorTotal = arithmeticHelper.round((valorRestante * p.percentual) / 100);
                        conta.dataVencimento = dataVencimento;

                        conta.observacao = `Vencimento em ${editDate(dataVencimento)}`;

                        this.model.lancamentosVinculados.push(conta);
                    });

                    const valorTotalRateado = this.model.lancamentosVinculados.reduce((acc, p) => acc + p.valorTotal, 0);
                    if (valorTotalRateado != valorTotal) {
                        const diferenca = arithmeticHelper.round(valorTotal - valorTotalRateado);
                        this.model.lancamentosVinculados[this.model.lancamentosVinculados.length - 1].valorTotal +=
                            diferenca;
                    }
                } else {
                    this.model.movimentacoes.push(this.pagamentoComponent.getModel());
                }
            }

            this.loadGridData();

            this.shortcutComponentRecebimento.hide();
        }
    }

    private onRemovePagamentoRecebimento(model: object) {
        this.$emit("remove-pagamento", model);
    }

    private async calcularSaldoParcela(lancamento: ContaReceberModel) {
        const valorJaPago = lancamento.movimentacoes.reduce((acc, p) => acc + p.valor, 0);
        lancamento.saldoGrid = lancamento.valorTotal - valorJaPago < 0 ? 0 : lancamento.valorTotal - valorJaPago;
    }

    public load(saldo: number, desconto: number, acrescimo: number) {
        this.saldoTotal = saldo;
        this.desconto = desconto;
        this.acrescimo = acrescimo;
    }

    public setModel(data: ContaReceberModel, vendafaturada?: boolean, tipoVendaId?: number) {
        this.model = data;
        this.vendafaturada = vendafaturada;
        this.tipoVenda = tipoVendaId;

        this.loadGridData();
    }

    private async mounted() {
        await this.$store.dispatch(SessionActions.LOAD_USUARIO_LOGADO);
        this.tipoVendaOptions = await tipoVendaService.comboCached(this.usuarioLogado.franquiaId);
        this.shortcutComponentParcela = this.$refs.shortcutComponentParcela as ShortcutComponent;
        this.shortcutComponentRecebimento = this.$refs.shortcutComponentRecebimento as ShortcutComponent;
        this.parcelaComponent = this.$refs.parcelaComponent as ParcelaComponent;
        this.pagamentoComponent = this.$refs.pagamentoComponent as PagamentoComponent;
    }

    private getDescricaoPorTipo(tipo: TipoReceitaArquivo): string {
        switch (tipo) {
            case TipoReceitaArquivo.Receita:
                return "Receita";
            case TipoReceitaArquivo.ComprovanteFinanceiro:
                return "Comprovante Financeiro";
            case TipoReceitaArquivo.AutenticacaoReceita:
                return "Autenticação de Receita";
            default:
                return "Receita";
        }
    }

    private formatarTextoArquivo(arquivo: ReceitaArquivoModel): string {
        let texto = "";
        if (arquivo.dataInclusao) {
            texto += editDateWithTime(arquivo.dataInclusao) + " - ";
        }
        texto += "[" + this.getDescricaoPorTipo(arquivo.tipo) + "]";
        if (arquivo.descricao && arquivo.descricao.trim() !== "") {
            texto += " " + arquivo.descricao;
        }
        return texto;
    }

    private onVisualizarArquivo(arquivo: ReceitaArquivoModel) {
        this.$emit("visualizar-arquivo", arquivo);
    }

    private onDownloadArquivo(arquivo: ReceitaArquivoModel) {
        this.$emit("download-arquivo", arquivo);
    }
}
